import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { QrCode, Download, Copy, ExternalLink } from "lucide-react";
import api from "@/lib/api";

/**
 * VendorQRCard — shown on the vendor's own profile page. Renders the QR
 * code that resolves to their public AddVendTour profile (`/vendors/{slug}`),
 * which itself supports booking, rating, and shows upcoming events.
 *
 * Backed by `GET /api/vendors/by-slug/{slug}/qr.png` — the image is streamed
 * from the backend with AddVendTour brand colors. Cached 24h by the browser.
 *
 * Actions:
 *   - Preview the code inline
 *   - Copy the profile URL (so it can be shared as a link)
 *   - Download the PNG for print/business cards
 *   - Open the public profile in a new tab to verify what customers see
 */
export default function VendorQRCard({ slug, businessName }) {
  const [busy, setBusy] = useState(false);
  // The QR's *encoded* URL uses backend FRONTEND_URL, which in dev may differ
  // from `window.location.origin`. Fetch the truth so we show the vendor the
  // exact URL scans will resolve to (avoids "why does the label say preview
  // but the QR takes me to prod" confusion).
  const [canonicalProfileUrl, setCanonicalProfileUrl] = useState("");
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    api.get(`/vendors/by-slug/${slug}/qr`)
      .then((r) => { if (alive) setCanonicalProfileUrl(r.data?.profile_url || ""); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);
  if (!slug) {
    return (
      <div className="card-warm rounded-sm p-5" data-testid="vendor-qr-card-empty">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4" style={{ color: "var(--avt-purple)" }} />
          <div className="overline" style={{ color: "var(--avt-purple)" }}>Your QR code</div>
        </div>
        <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
          Save your profile at least once to generate a shareable QR code for booking, rating, and event follows.
        </p>
      </div>
    );
  }

  // Build the QR image URL. Because REACT_APP_BACKEND_URL is absolute the
  // <img> renders correctly from any origin (mobile safari, PWA, print preview).
  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  // v2 cache-buster: an earlier deploy served QRs baked with the legacy
  // marketsstall.com URL under `immutable` cache headers. Bumping the query
  // string forces browsers + any CDN edge to re-fetch a fresh PNG.
  const qrUrl = `${backendOrigin}/api/vendors/by-slug/${slug}/qr.png?v=2`;
  // Fall back to `window.location.origin` while the canonical URL is loading
  // so the UI never renders an empty target box.
  const profileUrl = canonicalProfileUrl || `${window.location.origin}/vendors/${slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success("Profile link copied");
    } catch { toast.error("Copy failed"); }
  };

  const downloadPng = async () => {
    setBusy(true);
    try {
      // Fetch as blob and trigger download so the file arrives with the right
      // filename ("addvendtour-<slug>.png") rather than the raw endpoint path.
      const res = await api.get(`/vendors/by-slug/${slug}/qr.png?v=2`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `addvendtour-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("QR code downloaded");
    } catch {
      toast.error("Download failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-warm rounded-sm p-5" data-testid="vendor-qr-card">
      <div className="flex items-center gap-2">
        <QrCode className="h-4 w-4" style={{ color: "var(--avt-purple)" }} />
        <div className="overline" style={{ color: "var(--avt-purple)" }}>Your QR code</div>
      </div>
      <p className="text-sm mt-1 mb-4" style={{ color: "var(--ink-soft)" }}>
        Print it on business cards, tents, or a booth sign. Scanning takes anyone
        straight to your <strong>{businessName || "AddVendTour"}</strong> profile
        where they can book, rate, and see your upcoming events.
      </p>

      <div className="flex items-start gap-4 flex-wrap">
        <div
          className="p-3 rounded-sm flex-shrink-0"
          style={{ backgroundColor: "var(--paper-warm)", border: "1px solid var(--sand)" }}
        >
          <img
            src={qrUrl}
            alt={`QR code for ${businessName || slug}`}
            width={180}
            height={180}
            className="block"
            data-testid="vendor-qr-img"
            onError={(e) => {
              e.currentTarget.style.opacity = "0.3";
              e.currentTarget.setAttribute("title", "QR temporarily unavailable — refresh in a moment.");
            }}
          />
        </div>

        <div className="flex-1 min-w-[220px] space-y-2">
          <div className="text-xs" style={{ color: "var(--ink-soft)" }}>Scans to:</div>
          <div
            className="text-sm font-mono truncate p-2 rounded-sm"
            style={{ backgroundColor: "var(--sand)", color: "var(--ink)" }}
            data-testid="vendor-qr-target-url"
          >
            {profileUrl}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              size="sm"
              onClick={downloadPng}
              disabled={busy}
              data-testid="vendor-qr-download"
              className="btn-primary rounded-full"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {busy ? "Downloading…" : "Download PNG"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={copyLink}
              data-testid="vendor-qr-copy-link"
              className="rounded-full"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
            </Button>
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="vendor-qr-open"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border"
              style={{ borderColor: "var(--border-warm)", color: "var(--ink)" }}
            >
              <ExternalLink className="h-3.5 w-3.5" /> Preview
            </a>
          </div>
          <p className="text-[11px] pt-1" style={{ color: "var(--ink-soft)" }}>
            Right-click the QR to save from desktop. Prints crisply at up to 4&times;4&nbsp;inches
            thanks to high error-correction.
          </p>
        </div>
      </div>
    </div>
  );
}
